import { useState } from 'react';
import { menuData } from '../../mocks/menu';
import Footer from '../home/components/Footer';

const categories = [
  { id: 'poissons', label: 'Poissons & Fruits de mer', icon: 'ri-fish-line', color: 'from-teal-400 to-cyan-500', bg: 'bg-teal-50', accent: 'text-teal-700', border: 'border-teal-200' },
  { id: 'viandes', label: 'Viandes', icon: 'ri-restaurant-2-line', color: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', accent: 'text-amber-700', border: 'border-amber-200' },
  { id: 'signature', label: 'Signature', icon: 'ri-star-line', color: 'from-emerald-500 to-green-600', bg: 'bg-emerald-50', accent: 'text-emerald-700', border: 'border-emerald-200' },
  { id: 'desserts', label: 'Desserts', icon: 'ri-cake-3-line', color: 'from-rose-400 to-pink-500', bg: 'bg-rose-50', accent: 'text-rose-700', border: 'border-rose-200' },
];

const categoryIcons: Record<string, string> = {
  poissons: 'ri-fish-line',
  viandes: 'ri-restaurant-2-line',
  signature: 'ri-star-line',
  desserts: 'ri-cake-3-line',
};

export default function MenuPage() {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(`section-${id}`);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 font-sans">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-stone-100">
        <div className="w-full px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-lg"></i>
            </div>
            <span className="text-sm font-medium">Retour</span>
          </button>
          <div
            className="text-2xl font-serif font-bold text-gray-900 cursor-pointer"
            onClick={() => window.REACT_APP_NAVIGATE('/')}
          >
            Ke'iki
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center text-emerald-600">
              <i className="ri-restaurant-line text-lg"></i>
            </div>
            <span className="text-sm font-medium text-stone-600 hidden sm:inline">Notre Menu</span>
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <div className="relative pt-16 overflow-hidden">
        <div className="relative h-64 sm:h-80">
          <img
            src="https://public.readdy.ai/ai/img_res/10980fab80931efae2aab31903f19128.jpg"
            alt="Menu Ke'iki"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-emerald-300 text-sm font-medium tracking-widest uppercase mb-3">Restaurant Ke'iki — Moorea</p>
            <h1 className="text-5xl sm:text-6xl font-serif text-white mb-4" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}>
              Notre Menu
            </h1>
            <p className="text-white/90 text-lg font-light max-w-xl">
              Cuisine généreuse aux saveurs polynésiennes et françaises
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Category Nav */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap justify-center gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => scrollToSection(cat.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                activeSection === cat.id
                  ? `bg-gradient-to-r ${cat.color} text-white border-transparent shadow-md`
                  : `bg-white ${cat.border} ${cat.accent} hover:shadow-sm`
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`}></i>
              </div>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Menu Content */}
      <main className="max-w-6xl mx-auto px-6 py-16 space-y-20">
        {categories.map((cat) => {
          const items = menuData[cat.id as keyof typeof menuData] ?? [];
          return (
            <section key={cat.id} id={`section-${cat.id}`}>
              {/* Section Header */}
              <div className="flex items-center gap-4 mb-10">
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${cat.color} shadow-lg`}>
                  <i className={`${categoryIcons[cat.id]} text-2xl text-white`}></i>
                </div>
                <div>
                  <h2 className="text-3xl font-serif text-gray-900">{cat.label}</h2>
                  <p className="text-sm text-stone-400 mt-0.5">{items.length} plat{items.length > 1 ? 's' : ''}</p>
                </div>
                <div className="flex-1 h-px bg-stone-200 ml-4"></div>
              </div>

              {/* Items Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className={`group relative bg-white rounded-2xl border ${cat.border} p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5`}
                  >
                    <div className={`absolute top-0 left-0 w-1 h-full rounded-l-2xl bg-gradient-to-b ${cat.color}`}></div>
                    <div className="flex justify-between items-start gap-4 pl-2">
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-900 mb-1 leading-snug">
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-stone-500 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                      {item.price && (
                        <div className="flex-shrink-0 text-right">
                          <span className={`text-lg font-bold ${cat.accent} whitespace-nowrap`}>
                            {item.price}
                          </span>
                          <span className="block text-xs text-stone-400 font-normal">XPF</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        {/* Bottom CTA */}
        <div className="text-center py-12 border-t border-stone-200">
          <p className="text-stone-500 text-sm mb-6">Une question sur notre menu ? Appelez-nous !</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+68987735594"
              className="flex items-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-lg"></i>
              </div>
              <span>+689 87 73 55 94</span>
            </a>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="flex items-center gap-3 bg-white border border-stone-200 text-stone-700 hover:bg-stone-50 px-8 py-3.5 rounded-full font-semibold shadow-sm hover:shadow-md transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-line text-lg"></i>
              </div>
              <span>Retour à l'accueil</span>
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
